/**
 * Управление настройками чат-ассистента: system prompt + модель.
 * Сохраняется в БД (таблица settings), применяется к новым стримам без рестарта.
 */

import {
    Alert, Autocomplete, Button, Card, Grid, Group, Loader, NumberInput, Stack, Switch, Text, Textarea, Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
    fetchChatSettings, fetchOpenrouterModels, resetChatSettings, updateChatSettings,
    type ChatModelInfo, type ChatSettings, type MaxTokensSettings,
} from '../../lib/api-admin';
import { fmtDate } from './format';

const MAX_TOKENS_LABELS: { key: keyof MaxTokensSettings; title: string; hint: string }[] = [
    { key: 'aspect',  title: 'Нарратив (сравнение)', hint: 'per-aspect нарратив изменения в /compare' },
    { key: 'entity',  title: 'Сущность (сравнение)',  hint: 'per-entity нарратив изменения в /compare' },
    { key: 'rollup',  title: 'Сводка (сравнение)',    hint: 'итоговый roll-up нарратив в /compare' },
    { key: 'summary', title: 'Срез карточки',         hint: 'LLM-пересказ сущности в «Обзоре»' },
];

export function AdminChatSettingsPage(): JSX.Element {
    const qc = useQueryClient();
    const q = useQuery({ queryKey: ['admin-chat-settings'], queryFn: fetchChatSettings, retry: false });

    return (
        <Stack gap="md">
            <Title order={2}>Настройки чата</Title>
            <Alert color="blue" variant="light">
                Меняется на лету: новые сообщения подхватят настройки сразу. Текущие стримы
                продолжат работать со старыми. <Text component="span" fw={600}>Modify with care</Text> —
                промпт применяется ко всем пользователям.
            </Alert>
            {q.isLoading && <Loader />}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && <Form data={q.data} onChanged={() => qc.invalidateQueries({ queryKey: ['admin-chat-settings'] })} />}
        </Stack>
    );
}

/**
 * Выбор модели: живой каталог OpenRouter (tools-capable, кэш на сервере) +
 * свободный ввод в формате `provider/model` — новые модели доступны без
 * релиза. Группа «Рекомендуемые» — свежие модели крупных семейств в
 * разумной цене; под полем — контекст и цена выбранной.
 */
function ModelPicker({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
    const modelsQ = useQuery({
        queryKey:  ['admin-openrouter-models'],
        queryFn:   fetchOpenrouterModels,
        staleTime: 5 * 60_000,
        retry:     false,
    });
    const models = modelsQ.data?.models ?? [];
    const recommended = models.filter(m => m.recommended);
    const rest        = models.filter(m => !m.recommended);

    const data = models.length > 0
        ? [
            { group: `Рекомендуемые (${recommended.length})`, items: recommended.map(m => m.id) },
            { group: `Все с tool-calling (${rest.length})`,   items: rest.map(m => m.id) },
        ]
        : [];

    const selected = models.find(m => m.id === value);
    const formatOk = /^[\w.~-]+\/[\w.~-]+(:[\w-]+)?$/.test(value);

    return (
        <>
            <Autocomplete
                data={data}
                value={value}
                onChange={onChange}
                placeholder="provider/model — например deepseek/deepseek-v4-flash"
                limit={30}
                maxDropdownHeight={320}
                error={value && !formatOk ? 'Формат: provider/model' : undefined}
            />
            <Text fz="xs" c="dimmed" mt={4}>
                {modelsQ.isLoading && 'Загружаю каталог OpenRouter…'}
                {modelsQ.error && `Каталог недоступен (${(modelsQ.error as Error).message}) — свободный ввод работает.`}
                {selected && (
                    <>ctx {Math.round(selected.context / 1024)}k · ${selected.promptUsd.toFixed(2)}/M вход · ${selected.completionUsd.toFixed(2)}/M выход</>
                )}
                {!selected && !modelsQ.isLoading && !modelsQ.error && formatOk && value &&
                    'Модели нет в каталоге tool-capable — проверь id: без tool-calling чат не работает.'}
            </Text>
        </>
    );
}

function Form({ data, onChanged }: { data: ChatSettings; onChanged: () => void }): JSX.Element {
    const [systemPrompt, setSystemPrompt] = useState(data.systemPrompt);
    const [model,        setModel]        = useState(data.model);
    // Срез карточки. summaryAsChat = «как у чата» (пустой summaryModel).
    const [summaryAsChat, setSummaryAsChat] = useState(data.summaryModel === '');
    const [summaryModel,  setSummaryModel]  = useState(data.summaryModel || data.model);
    const [summaryPrompt, setSummaryPrompt] = useState(data.summarySystemPrompt);
    const [maxTokens,     setMaxTokens]     = useState<MaxTokensSettings>(data.maxTokens);

    useEffect(() => {
        setSystemPrompt(data.systemPrompt); setModel(data.model);
        setSummaryAsChat(data.summaryModel === '');
        setSummaryModel(data.summaryModel || data.model);
        setSummaryPrompt(data.summarySystemPrompt);
        setMaxTokens(data.maxTokens);
    }, [data]);

    const save = useMutation({
        mutationFn: (patch: {
            systemPrompt?: string; model?: string; summaryModel?: string; summarySystemPrompt?: string;
            maxTokens?: Partial<MaxTokensSettings>;
        }) => updateChatSettings(patch),
        onSuccess:  onChanged,
    });
    const resetPrompt = useMutation({
        mutationFn: () => resetChatSettings(['systemPrompt']),
        onSuccess:  onChanged,
    });
    const resetModel = useMutation({
        mutationFn: () => resetChatSettings(['model']),
        onSuccess:  onChanged,
    });
    const resetSummaryPrompt = useMutation({
        mutationFn: () => resetChatSettings(['summarySystemPrompt']),
        onSuccess:  onChanged,
    });
    const resetMaxTokens = useMutation({
        mutationFn: (key: keyof MaxTokensSettings) => resetChatSettings([`maxTokens.${key}` as const]),
        onSuccess:  onChanged,
    });

    const promptChanged = systemPrompt !== data.systemPrompt;
    const modelChanged  = model        !== data.model;
    // «как у чата» → сохраняем пустую строку (сервер удалит override).
    const summaryModelToSave  = summaryAsChat ? '' : summaryModel;
    const summaryModelChanged = summaryModelToSave !== data.summaryModel;
    const summaryPromptChanged = summaryPrompt !== data.summarySystemPrompt;
    const maxTokensChanged = (key: keyof MaxTokensSettings): boolean => maxTokens[key] !== data.maxTokens[key];

    return (
        <Stack gap="lg">
            <Card withBorder padding="md">
                <Group justify="space-between" align="end" wrap="wrap" mb="xs">
                    <Title order={5}>Модель</Title>
                    {data.updated.model && (
                        <Text fz="xs" c="dimmed">изменено {fmtDate(data.updated.model.at)} ({data.updated.model.by ?? '—'})</Text>
                    )}
                </Group>
                <ModelPicker value={model} onChange={setModel} />
                <Text fz="xs" c="dimmed" mt={4}>Дефолт: <code>{data.defaults.model}</code></Text>
                <Group justify="end" mt="sm">
                    <Button variant="subtle" onClick={() => resetModel.mutate()} loading={resetModel.isPending}
                            disabled={model === data.defaults.model && !data.updated.model}>
                        Сбросить к дефолту
                    </Button>
                    <Button onClick={() => save.mutate({ model })} disabled={!modelChanged} loading={save.isPending}>
                        Сохранить модель
                    </Button>
                </Group>
            </Card>

            <Card withBorder padding="md">
                <Group justify="space-between" align="end" wrap="wrap" mb="xs">
                    <Title order={5}>System prompt</Title>
                    {data.updated.systemPrompt && (
                        <Text fz="xs" c="dimmed">изменено {fmtDate(data.updated.systemPrompt.at)} ({data.updated.systemPrompt.by ?? '—'})</Text>
                    )}
                </Group>
                <Textarea
                    value={systemPrompt}
                    onChange={e => setSystemPrompt(e.currentTarget.value)}
                    autosize
                    minRows={10}
                    maxRows={30}
                    styles={{ input: { fontFamily: 'monospace', fontSize: 13 } }}
                />
                <Group justify="space-between" mt="sm" wrap="wrap">
                    <Button variant="subtle" size="xs" onClick={() => setSystemPrompt(data.defaults.systemPrompt)}>
                        Подставить дефолт (без сохранения)
                    </Button>
                    <Group gap="xs">
                        <Button variant="subtle" onClick={() => resetPrompt.mutate()} loading={resetPrompt.isPending}
                                disabled={!data.updated.systemPrompt}>
                            Сбросить к дефолту
                        </Button>
                        <Button onClick={() => save.mutate({ systemPrompt })} disabled={!promptChanged} loading={save.isPending}>
                            Сохранить промпт
                        </Button>
                    </Group>
                </Group>
            </Card>

            {/* ── Срез карточки сущности ─────────────────────────────── */}
            <Card withBorder padding="md">
                <Group justify="space-between" align="end" wrap="wrap" mb="xs">
                    <Title order={5}>Срез карточки — модель</Title>
                    {data.updated.summaryModel && (
                        <Text fz="xs" c="dimmed">изменено {fmtDate(data.updated.summaryModel.at)} ({data.updated.summaryModel.by ?? '—'})</Text>
                    )}
                </Group>
                <Text fz="xs" c="dimmed" mb="xs">
                    LLM-пересказ сущности в «Обзоре» карточки. Кэш — общий для репозитория,
                    сбрасывается при изменении сущности, модели или промпта.
                </Text>
                <Switch
                    label="Как у чата"
                    checked={summaryAsChat}
                    onChange={e => setSummaryAsChat(e.currentTarget.checked)}
                    mb="xs"
                />
                {!summaryAsChat && <ModelPicker value={summaryModel} onChange={setSummaryModel} />}
                <Group justify="end" mt="sm">
                    <Button onClick={() => save.mutate({ summaryModel: summaryModelToSave })}
                            disabled={!summaryModelChanged} loading={save.isPending}>
                        Сохранить модель среза
                    </Button>
                </Group>
            </Card>

            <Card withBorder padding="md">
                <Group justify="space-between" align="end" wrap="wrap" mb="xs">
                    <Title order={5}>Срез карточки — промпт</Title>
                    {data.updated.summarySystemPrompt && (
                        <Text fz="xs" c="dimmed">изменено {fmtDate(data.updated.summarySystemPrompt.at)} ({data.updated.summarySystemPrompt.by ?? '—'})</Text>
                    )}
                </Group>
                <Textarea
                    value={summaryPrompt}
                    onChange={e => setSummaryPrompt(e.currentTarget.value)}
                    autosize minRows={6} maxRows={20}
                    styles={{ input: { fontFamily: 'monospace', fontSize: 13 } }}
                />
                <Group justify="space-between" mt="sm" wrap="wrap">
                    <Button variant="subtle" size="xs" onClick={() => setSummaryPrompt(data.defaults.summarySystemPrompt)}>
                        Подставить дефолт (без сохранения)
                    </Button>
                    <Group gap="xs">
                        <Button variant="subtle" onClick={() => resetSummaryPrompt.mutate()} loading={resetSummaryPrompt.isPending}
                                disabled={!data.updated.summarySystemPrompt}>
                            Сбросить к дефолту
                        </Button>
                        <Button onClick={() => save.mutate({ summarySystemPrompt: summaryPrompt })}
                                disabled={!summaryPromptChanged} loading={save.isPending}>
                            Сохранить промпт среза
                        </Button>
                    </Group>
                </Group>
            </Card>

            <Card withBorder padding="md">
                <Title order={5} mb="xs">Бюджеты токенов (max_tokens)</Title>
                <Text fz="xs" c="dimmed" mb="sm">
                    Пусто = без ограничения (решает провайдер/модель). При обрезке ответа
                    (finish_reason=length) сервер сам повторяет с увеличенным бюджетом и не
                    кэширует половинчатый текст — но заданный тут потолок всё равно уважается.
                </Text>
                <Grid>
                    {MAX_TOKENS_LABELS.map(({ key, title, hint }) => (
                        <Grid.Col span={{ base: 12, sm: 6 }} key={key}>
                            <Group justify="space-between" align="end" wrap="wrap" mb={2}>
                                <Text fz="sm" fw={600}>{title}</Text>
                                {data.updated.maxTokens[key] && (
                                    <Text fz="xs" c="dimmed">
                                        {fmtDate(data.updated.maxTokens[key]!.at)} ({data.updated.maxTokens[key]!.by ?? '—'})
                                    </Text>
                                )}
                            </Group>
                            <Text fz="xs" c="dimmed" mb={4}>{hint}</Text>
                            <Group gap="xs" align="end" wrap="nowrap">
                                <NumberInput
                                    flex={1}
                                    placeholder="без ограничения"
                                    min={1} max={1_000_000} step={100}
                                    value={maxTokens[key] === '' ? '' : Number(maxTokens[key])}
                                    onChange={v => setMaxTokens(prev => ({ ...prev, [key]: v === '' ? '' : String(v) }))}
                                />
                                <Button size="xs" variant="subtle" color="gray"
                                        onClick={() => setMaxTokens(prev => ({ ...prev, [key]: '' }))}
                                        disabled={maxTokens[key] === ''}>
                                    Снять лимит
                                </Button>
                            </Group>
                            <Group justify="end" mt={6} gap="xs">
                                <Button size="xs" variant="subtle" onClick={() => resetMaxTokens.mutate(key)}
                                        loading={resetMaxTokens.isPending} disabled={!data.updated.maxTokens[key]}>
                                    Сбросить
                                </Button>
                                <Button size="xs" onClick={() => save.mutate({ maxTokens: { [key]: maxTokens[key] } })}
                                        disabled={!maxTokensChanged(key)} loading={save.isPending}>
                                    Сохранить
                                </Button>
                            </Group>
                        </Grid.Col>
                    ))}
                </Grid>
            </Card>

            {save.error  && <Alert color="red">{(save.error  as Error).message}</Alert>}
            {(resetPrompt.error || resetModel.error || resetSummaryPrompt.error || resetMaxTokens.error) && (
                <Alert color="red">{((resetPrompt.error ?? resetModel.error ?? resetSummaryPrompt.error ?? resetMaxTokens.error) as Error).message}</Alert>
            )}
        </Stack>
    );
}
